import type { AssetKind } from './types';

const MIB = 1024 * 1024;

export interface BrowserMediaMetadata {
  width?: number;
  height?: number;
  duration?: number;
}

export function videoMetadata(file: Blob): Promise<Required<BrowserMediaMetadata>> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    const cleanup = (): void => URL.revokeObjectURL(url);
    video.addEventListener(
      'loadedmetadata',
      () => {
        const result = {
          width: video.videoWidth,
          height: video.videoHeight,
          duration: video.duration,
        };
        cleanup();
        resolve(result);
      },
      { once: true },
    );
    video.addEventListener(
      'error',
      () => {
        cleanup();
        reject(new Error('Браузер не смог прочитать этот видеофайл.'));
      },
      { once: true },
    );
    video.src = url;
  });
}

async function imageMetadata(file: Blob): Promise<BrowserMediaMetadata> {
  const bitmap = await createImageBitmap(file);
  try {
    return { width: bitmap.width, height: bitmap.height };
  } finally {
    bitmap.close();
  }
}

export async function inspectAsset(
  kind: AssetKind,
  file: File,
): Promise<BrowserMediaMetadata> {
  const extension = file.name.toLowerCase().split('.').pop() ?? '';
  const maximumBytes =
    kind === 'logo'
      ? 5 * MIB
      : kind === 'frame'
        ? (extension === 'mov' ? 50 : 10) * MIB
        : 20 * MIB;
  if (!file.size || file.size > maximumBytes) {
    if (kind === 'frame') {
      throw new Error(
        extension === 'mov'
          ? 'Анимированная рамка слишком большая. Уменьшите MOV до 50 МБ.'
          : 'Статичная рамка слишком большая. Уменьшите файл до 10 МБ.',
      );
    }
    throw new Error(`Лимит файла: ${Math.round(maximumBytes / MIB)} МБ.`);
  }
  if (kind === 'logo' && !['png', 'webp'].includes(extension)) {
    throw new Error('Логотип должен быть PNG или WebP.');
  }
  if (kind === 'frame' && !['png', 'webp', 'mov'].includes(extension)) {
    throw new Error('Рамка должна быть PNG, WebP или MOV с alpha-каналом.');
  }
  if (kind === 'outro' && extension !== 'mp4') {
    throw new Error('Заглушка должна быть MP4.');
  }

  if (['png', 'webp'].includes(extension)) {
    return imageMetadata(file);
  }

  // Telegram WebView often cannot decode QuickTime/ProRes MOV in a <video>
  // element even though FFmpeg can process it. Do not block the upload on a
  // browser metadata probe; the frame normalizer is the source of truth.
  if (kind === 'frame' && extension === 'mov') {
    return {};
  }

  const metadata = await videoMetadata(file);
  if (kind === 'outro' && (metadata.duration < 0.25 || metadata.duration > 10)) {
    throw new Error('Заглушка должна длиться от 0,25 до 10 секунд.');
  }
  return metadata;
}

export function formatBytes(bytes: number): string {
  if (bytes < MIB) {
    return `${Math.max(1, Math.round(bytes / 1024))} КБ`;
  }
  return `${(bytes / MIB).toFixed(1).replace('.0', '')} МБ`;
}
